import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
    Link as RouterLink,
    useLocation,
    useNavigate,
    useParams,
} from 'react-router-dom';
import {
    Box,
    Button,
    ButtonBase,
    CircularProgress,
    Skeleton,
    Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

import videocallshopApi from '../../apis/videocallshop';
import { fetchStores } from '../../actions/storeActions';
import { isStoreOpen } from '../../hooks/useStoreHours';
import ProductCard from './ProductCard';
import CartButton from '../cart/CartButton';
import {
    COLORS,
    FONT_BODY,
    FONT_TITLE,
    FONTS_HREF,
    secondaryButtonSx,
    skeletonSx,
} from './storefrontStyles';

const PAGE_SIZE = 24;

const gridSx = {
    display: 'grid',
    gridTemplateColumns: {
        xs: 'repeat(2, 1fr)',
        sm: 'repeat(3, 1fr)',
        md: 'repeat(4, 1fr)',
    },
    gap: { xs: 1.5, md: 2 },
};

// "Ver todo" de una categoría del home: sus tiendas (abiertas primero) y todos
// sus productos, de a PAGE_SIZE con "Ver más" (GET /products/featured?categoryId=).
// Los productos no tienen categoría: son los de las tiendas de la categoría.
const CategoryPage = ({ stores, fetchStores }) => {
    const { categoryId } = useParams();
    const navigate = useNavigate();
    // el nombre viene del home (state) para no esperar a /stores
    const location = useLocation();
    const id = Number(categoryId);
    const [items, setItems] = useState([]);
    const [nextOffset, setNextOffset] = useState(0);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        fetchStores();
    }, [fetchStores]);

    const load = useCallback(
        (offset) => {
            setLoading(true);
            setFailed(false);
            videocallshopApi
                .get('/products/featured', {
                    params: { categoryId: id, limit: PAGE_SIZE, offset },
                })
                .then(({ data }) => {
                    const page = data.data || {};
                    setItems((cur) =>
                        offset === 0
                            ? page.items || []
                            : [...cur, ...(page.items || [])]
                    );
                    setNextOffset(page.hasMore ? page.nextOffset : null);
                })
                .catch(() => setFailed(true))
                .finally(() => setLoading(false));
        },
        [id]
    );

    useEffect(() => {
        load(0);
    }, [load]);

    const categoryStores = useMemo(
        () =>
            Object.values(stores.stores)
                .filter((s) => s.categoryId === id)
                .map((s) => ({ ...s, isOpen: isStoreOpen(s) }))
                .sort(
                    (a, b) =>
                        Number(b.isOpen) - Number(a.isOpen) ||
                        a.name.localeCompare(b.name, 'es')
                ),
        [stores.stores, id]
    );
    const name =
        categoryStores[0]?.categoryName || location.state?.name || 'Categoría';

    const goToStore = (store) =>
        navigate(
            store.productCount > 0
                ? `/tienda/${store.storeId}`
                : `/${store.storeId}/create-call-request`
        );

    return (
        <Box
            sx={{
                width: '100%',
                minHeight: '100vh',
                backgroundColor: COLORS.background,
                color: COLORS.text,
            }}
        >
            <link href={FONTS_HREF} rel="stylesheet" />
            <Box
                sx={{
                    maxWidth: 1200,
                    mx: 'auto',
                    px: { xs: 2, md: 4 },
                    py: { xs: 2, md: 4 },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: 2,
                    }}
                >
                    <Button
                        component={RouterLink}
                        to="/home"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            color: COLORS.muted,
                            '&:hover': { color: 'white' },
                        }}
                    >
                        Inicio
                    </Button>
                    <CartButton />
                </Box>

                <Typography
                    component="h1"
                    sx={{
                        fontFamily: FONT_TITLE,
                        fontWeight: 800,
                        fontSize: { xs: '1.6rem', md: '2.2rem' },
                        mb: 2,
                    }}
                >
                    {name}
                </Typography>

                {categoryStores.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            flexWrap: 'wrap',
                            mb: 3,
                        }}
                    >
                        {categoryStores.map((store) => (
                            <ButtonBase
                                key={store.storeId}
                                onClick={() => goToStore(store)}
                                sx={{
                                    gap: 1,
                                    px: 1.5,
                                    py: 0.75,
                                    borderRadius: '20px',
                                    backgroundColor: COLORS.card,
                                    border: `1px solid ${COLORS.border}`,
                                    color: COLORS.text,
                                    fontFamily: FONT_BODY,
                                    fontSize: '0.85rem',
                                    fontWeight: 600,
                                }}
                            >
                                {store.name}
                                <Box
                                    sx={{
                                        width: 7,
                                        height: 7,
                                        borderRadius: '50%',
                                        backgroundColor: store.isOpen
                                            ? COLORS.open
                                            : COLORS.subtle,
                                    }}
                                />
                            </ButtonBase>
                        ))}
                    </Box>
                )}

                {loading && items.length === 0 ? (
                    <Box sx={gridSx}>
                        {Array.from({ length: 8 }, (_, i) => (
                            <Skeleton
                                key={i}
                                variant="rounded"
                                height={260}
                                sx={skeletonSx}
                            />
                        ))}
                    </Box>
                ) : failed && items.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <Typography
                            sx={{
                                color: COLORS.muted,
                                fontFamily: FONT_BODY,
                                mb: 2,
                            }}
                        >
                            No pudimos cargar los productos.
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={() => load(0)}
                            sx={secondaryButtonSx}
                        >
                            Reintentar
                        </Button>
                    </Box>
                ) : items.length === 0 ? (
                    <Typography
                        sx={{
                            color: COLORS.muted,
                            fontFamily: FONT_BODY,
                            textAlign: 'center',
                            py: 6,
                        }}
                    >
                        Las tiendas de esta categoría todavía no cargaron
                        productos.
                    </Typography>
                ) : (
                    <>
                        <Box sx={gridSx}>
                            {items.map((product) => (
                                <ProductCard
                                    key={product.productId}
                                    product={product}
                                    store={product.store}
                                />
                            ))}
                        </Box>
                        {nextOffset !== null && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    mt: 3,
                                }}
                            >
                                {loading ? (
                                    <CircularProgress
                                        size={28}
                                        sx={{ color: COLORS.accent }}
                                    />
                                ) : (
                                    <Button
                                        variant="outlined"
                                        onClick={() => load(nextOffset)}
                                        sx={secondaryButtonSx}
                                    >
                                        Ver más
                                    </Button>
                                )}
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

const mapStateToProps = (state) => ({ stores: state.stores });

export default connect(mapStateToProps, { fetchStores })(CategoryPage);
